import {
    OnInit,
    AfterViewInit,
    Attribute,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    forwardRef,
    HostBinding,
    Input,
    NgZone,
    Output,
    ViewChild,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { coerceBooleanProperty, coerceNumberProperty } from '@angular/cdk/coercion';
import { FocusMonitor, FocusOrigin } from '@angular/cdk/a11y';

export const ICC_CHECKBOX_CONTROL_VALUE_ACCESSOR: any = {
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => CheckboxComponent),
    multi: true,
};

export interface IccCheckboxChange {
    source: CheckboxComponent;
    checked: boolean;
}

let nextUniqueId = 0;

@Component({
    selector: 'icc-checkbox',
    templateUrl: './checkbox.component.html',
    styleUrls: ['./checkbox.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [ICC_CHECKBOX_CONTROL_VALUE_ACCESSOR],
})
export class CheckboxComponent implements OnInit, ControlValueAccessor, AfterViewInit {
    private _tabIndex = 0;
    defaultTabIndex = 0;
    private _size: 'large' | 'medium' | 'small' = 'large';

    get tabIndex(): number {
        return this.disabled ? -1 : this._tabIndex;
    }
    set tabIndex(value: number) {
        // If the specified tabIndex value is null or undefined, fall back to the default value.
        this._tabIndex = value != null ? coerceNumberProperty(value) : this.defaultTabIndex;
    }
    /**
     * Attached to the aria-label attribute of the host element. In most cases, aria-labelledby will
     * take precedence so this may be omitted.
     */
    @Input('aria-label') ariaLabel = '';

    /**
     * Users can specify the `aria-labelledby` attribute which will be forwarded to the input element
     */
    @Input('aria-labelledby') ariaLabelledby: string | null = null;

    /** The 'aria-describedby' attribute is read after the element's label and field type. */
    @Input('aria-describedby') ariaDescribedby!: string;

    private _uniqueId = `icc-checkbox-${++nextUniqueId}`;

    /** A unique id for the checkbox input. If none is supplied, it will be auto-generated. */
    @Input() id: string = this._uniqueId;

    /** Returns the unique id for the visual hidden input. */
    get inputId(): string {
        return `${this.id || this._uniqueId}-input`;
    }

    @Input()
    get required(): boolean {
        return this._required;
    }
    set required(value: boolean) {
        this._required = coerceBooleanProperty(value);
    }
    private _required = false;

    /**
     * Whether the checkbox is checked.
     */
    @HostBinding('class.icc-checkbox-checked')
    @Input()
    get checked(): boolean {
        return this._checked;
    }
    set checked(value: boolean) {
        const checked = coerceBooleanProperty(value);

        if (checked !== this.checked) {
            this._checked = checked;
            this._changeDetectorRef.markForCheck();
        }
    }
    private _checked = false;

    /**
     * Whether the checkbox is disabled. This fully overrides the implementation provided by
     * mixinDisabled, but the mixin is still required because mixinTabIndex requires it.
     */
    @HostBinding('class.icc-checkbox-disabled')
    @Input()
    get disabled(): boolean {
        return this._disabled;
    }
    set disabled(value: boolean) {
        const newValue = coerceBooleanProperty(value);

        if (newValue !== this.disabled) {
            this._disabled = newValue;
            this._changeDetectorRef.markForCheck();
        }
    }
    private _disabled = false;

    /**
     * Whether the checkbox is indeterminate. This is also known as "mixed" mode and can be used to
     * represent a checkbox with three states, e.g. a checkbox that represents a nested list of
     * checkable items. Note that whenever checkbox is manually clicked, indeterminate is immediately
     * set to false.
     */
    @HostBinding('class.icc-checkbox-indeterminate')
    @Input()
    get indeterminate(): boolean {
        return this._indeterminate;
    }
    set indeterminate(value: boolean) {
        const changed = value !== this._indeterminate;
        this._indeterminate = coerceBooleanProperty(value);

        if (changed) {
            this.indeterminateChange.emit(this._indeterminate);
        }

        this._syncIndeterminate(this._indeterminate);
    }
    private _indeterminate = false;

    @Input()
    get size() {
        return this._size;
    }
    set size(size: 'large' | 'medium' | 'small') {
        this._size = size;
    }

    @Input() name: string | null = null;

    // eslint-disable-next-line @angular-eslint/no-output-native
    @Output()
    readonly change: EventEmitter<IccCheckboxChange> = new EventEmitter<IccCheckboxChange>();

    /** Event emitted when the checkbox's `indeterminate` value changes. */
    @Output() readonly indeterminateChange: EventEmitter<boolean> = new EventEmitter<boolean>();

    /** The value attribute of the native input element */
    @Input() value!: string;

    @ViewChild('input') _inputElement!: ElementRef<HTMLInputElement>;

    /**
     * Called when the checkbox is blurred. Needed to properly implement ControlValueAccessor.
     * @docs-private
     */
    _onTouched: () => any = () => {};

    private _controlValueAccessorChangeFn: (value: any) => void = () => {};

    constructor(
        private _elementRef: ElementRef,
        elementRef: ElementRef<HTMLElement>,
        private _changeDetectorRef: ChangeDetectorRef,
        private _focusMonitor: FocusMonitor,
        private _ngZone: NgZone,
        @Attribute('tabindex') tabIndex: string
    ) {
        this.tabIndex = parseInt(tabIndex) || 0;
    }

    writeValue(value: any): void {
        this.checked = !!value;
    }
    registerOnChange(fn: (_: any) => {}): void {
        this._controlValueAccessorChangeFn = fn;
    }
    registerOnTouched(fn: () => {}): void {
        this._onTouched = fn;
    }
    setDisabledState(isDisabled: boolean): void {
        this.disabled = isDisabled;
    }

    ngOnInit(){
        this.handleSize();
        this.handleClasses();
    }

    ngAfterViewInit() {
        this._focusMonitor.monitor(this._elementRef, true).subscribe((focusOrigin: FocusOrigin) => {
            if (!focusOrigin) {
                // When a focused element becomes disabled, the browser *immediately* fires a blur event.
                // Angular does not expect events to be raised during change detection, so any state change
                // (such as a form control's 'ng-touched') will cause a changed-after-checked error.
                // See https://github.com/angular/angular/issues/17793. To work around this, we defer
                // telling the form control it has been touched until the next tick.
                Promise.resolve().then(() => {
                    this._onTouched();
                    this._changeDetectorRef.markForCheck();
                });
            }
        });

        this._syncIndeterminate(this._indeterminate);
    }

    ngOnDestroy() {
        this._focusMonitor.stopMonitoring(this._elementRef);
    }

    /** Method being called whenever the label text changes. */
    _onLabelTextChange() {
        // Since the event of the `cdkObserveContent` directive runs outside of the zone, the checkbox
        // component will be only marked for check, but no actual change detection runs automatically.
        // Instead of going back into the zone in order to trigger a change detection which causes
        // *all* components to be checked (if explicitly marked or not using OnPush), we only trigger
        // an explicit change detection for the checkbox view and its children.
        this._changeDetectorRef.detectChanges();
    }

    _getAriaChecked(): 'true' | 'false' | 'mixed' {
        if (this.checked) {
            return 'true';
        }

        return this.indeterminate ? 'mixed' : 'false';
    }

    private _emitChangeEvent() {
        const event: IccCheckboxChange = {
            source: this,
            checked: this.checked,
        };

        this._controlValueAccessorChangeFn(this.checked);
        this.change.emit(event);

        // Assigning the value again here is redundant, but we have to do it in case it was
        // changed inside the `change` listener which will cause the input to be out of sync.
        if (this._inputElement) {
            this._inputElement.nativeElement.checked = this.checked;
        }
    }

    /** Toggles the `checked` state of the checkbox. */
    toggle(): void {
        this.checked = !this.checked;
        this._controlValueAccessorChangeFn(this.checked);
    }

    /**
     * Event handler for checkbox input element.
     * Toggles checked state if element is not disabled.
     * Do not toggle on (change) event since IE doesn't fire change event when
     *   indeterminate checkbox is clicked.
     * @param event
     */
    _onInputClick(event: Event) {
        const clickAction: 'check' | 'noop' = 'check';

        // We have to stop propagation for click events on the visual hidden input element.
        // By default, when a user clicks on a label element, a generated click event will be
        // dispatched on the associated input element. Since we are using a label element as our
        // root container, the click event on the `checkbox` will be executed twice.
        // The real click event will bubble up, and the generated click event also tries to bubble up.
        // This will lead to multiple click events.
        // Preventing bubbling for the second event will solve that issue.
        event.stopPropagation();

        // If resetIndeterminate is false, and the current state is indeterminate, do nothing on click
        if (!this.disabled) {
            // When user manually click on the checkbox, `indeterminate` is set to false.
            if (this.indeterminate && clickAction !== 'check') {
                Promise.resolve().then(() => {
                    this._indeterminate = false;
                    this.indeterminateChange.emit(this._indeterminate);
                });
            }

            this._checked = !this._checked;

            // Emit our custom change event if the native input emitted one.
            // It is important to only emit it, if the native input triggered one, because
            // we don't want to trigger a change event, when the `checked` variable changes for example.
            this._emitChangeEvent();
        }
    }

    /** Focuses the checkbox. */
    focus(origin?: FocusOrigin, options?: FocusOptions): void {
        if (origin) {
            this._focusMonitor.focusVia(this._inputElement, origin, options);
        } else {
            this._inputElement.nativeElement.focus(options);
        }
    }

    _onInteractionEvent(event: Event) {
        // We always have to stop propagation on the change event.
        // Otherwise the change event, from the input element, will bubble up and
        // emit its event object to the `change` output.
        event.stopPropagation();
    }

    /**
     * Syncs the indeterminate value with the checkbox DOM node.
     *
     * We sync `indeterminate` directly on the DOM node, because in Ivy the check for whether a
     * property is supported on an element boils down to `if (propName in element)`. Domino's
     * HTMLInputElement doesn't have an `indeterminate` property so Ivy will warn during
     * server-side rendering.
     */
    private _syncIndeterminate(value: boolean) {
        const nativeCheckbox = this._inputElement;

        if (nativeCheckbox) {
            nativeCheckbox.nativeElement.indeterminate = value;
        }
    }

    protected handleClasses() {
        if (this._elementRef?.nativeElement) {
            const element = this._elementRef.nativeElement as HTMLElement;

            element.classList.add('icc-size-' + this._size);

            if (this._disabled) {
                element.classList.add('icc-disabled');
            }
            if (this._checked) {
                element.classList.add('icc-checked');
            }
        }
    }

    protected handleSize() {
        if (this._elementRef?.nativeElement?.classList?.value) {
            const classes = this._elementRef?.nativeElement?.classList;
            if (classes.value.includes('icc-size-large')) {
                this._size = 'large';
            }
            if (classes.value.includes('icc-size-medium')) {
                this._size = 'medium';
            }
            if (classes.value.includes('icc-size-small')) {
                this._size = 'small';
            }
        }
    }
}
